import React from "react";
import { Easing, Platform, Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import { MotiView } from "moti";
import { ArrowRightIcon, QuestionIcon, WarningIcon } from "@phosphor-icons/react";

import Tooltip from "../../../../../components/common/tooltip/Tooltip";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { CourseEvaluatorReviewData } from "../types/reviewTypes";
import { InformationalBadge } from "./DimensionCell";
import { GAP_DIMENSIONS, getDimensionDotColor, TONE_COLORS, type GapAnalysisDimension } from "./gapAnalysisConstants";

interface GapAnalysisDimensionRailProps {
  review: CourseEvaluatorReviewData;
  dimensions?: GapAnalysisDimension[];
  selectedDimensionId: string | null;
  onSelectDimension: (dimensionId: string) => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
  headerLabel?: string;
}

function GapAnalysisDimensionRail({
  review,
  dimensions = GAP_DIMENSIONS,
  selectedDimensionId,
  onSelectDimension,
  onJumpToEntity,
  headerLabel = "Quality dimensions",
}: GapAnalysisDimensionRailProps) {
  const [expandedDimensionId, setExpandedDimensionId] = React.useState<string | null>(selectedDimensionId);

  React.useEffect(() => {
    setExpandedDimensionId(selectedDimensionId);
  }, [selectedDimensionId]);

  return (
    <View style={styles.rail}>
      <View style={styles.summaryHeader}>
        <View style={styles.summaryScoreGroup}>
          <CustomText
            text={headerLabel}
            useTranslationText={false}
            size="s"
            weight="medium"
            style={styles.summaryScore}
          />
        </View>
      </View>

      <View style={styles.dimensionList}>
        {dimensions.map((dimension) => (
          <DimensionRailItem
            key={dimension.id}
            dimension={dimension}
            isSelected={dimension.id === expandedDimensionId}
            onSelectDimension={(dimensionId) => {
              setExpandedDimensionId((currentDimensionId) => (currentDimensionId === dimensionId ? null : dimensionId));
              onSelectDimension(dimensionId);
            }}
            onJumpToEntity={onJumpToEntity}
          />
        ))}
      </View>
    </View>
  );
}

function DimensionRailItem({
  dimension,
  isSelected,
  onSelectDimension,
  onJumpToEntity,
}: {
  dimension: GapAnalysisDimension;
  isSelected: boolean;
  onSelectDimension: (dimensionId: string) => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
}) {
  const toneColor = TONE_COLORS[dimension.tone];
  const dotColor = getDimensionDotColor(dimension);
  const emptyStateMessage = getEmptyStateMessage(dimension);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${dimension.label}, ${dimension.informational ? "informational only, " : ""}${
        dimension.issues
      } ${dimension.issues === 1 ? "item" : "items"}`}
      accessibilityState={{ selected: isSelected }}
      onPress={() => onSelectDimension(dimension.id)}
      style={(state) => [
        styles.dimensionButton,
        isSelected && styles.dimensionButtonSelected,
        getButtonStateStyle(state),
      ]}>
      <View style={[styles.selectedAccent, { backgroundColor: toneColor, opacity: isSelected ? 1 : 0 }]} />
      <View style={styles.dimensionTopRow}>
        <View style={[styles.dimensionDot, { backgroundColor: dotColor }]} />
        <View style={styles.dimensionLabelGroup}>
          <CustomText
            text={dimension.label}
            useTranslationText={false}
            size="s"
            weight="medium"
            numberOfLines={1}
            style={styles.dimensionLabel}
          />
          <Tooltip
            content={dimension.tooltip}
            maxWidth={320}
            offset={8}
            showArrow={false}
            textNumberOfLines={6}
            tooltipStyle={styles.dimensionTooltip}
            tooltipTextStyle={styles.dimensionTooltipText}
            wrapText={true}
            wrapTextAfterLength={50}
            enableOnSmallScreens={true}>
            <View
              accessibilityRole="image"
              accessibilityLabel={`What ${dimension.label} checks: ${dimension.tooltip}`}
              style={styles.dimensionHelpIcon}>
              <QuestionIcon size={13} color={colors.grey[400]} weight="bold" />
            </View>
          </Tooltip>
        </View>

        <View style={styles.dimensionScoreGroup}>{dimension.informational && <InformationalBadge />}</View>
      </View>
      {isSelected && (
        <MotiView
          from={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ type: "timing", duration: 220, easing: Easing.out(Easing.ease) }}
          style={styles.issuePanelContainer}>
          <View style={styles.issuePanelBody}>
            {dimension.id === "accessibility" && (
              <View style={styles.accessibilityScopeNotice}>
                <View style={styles.accessibilityScopeIcon}>
                  <WarningIcon size={16} color={colors.warning[700]} weight="bold" />
                </View>
                <View style={styles.accessibilityScopeTextGroup}>
                  <CustomText
                    text="Limited scope — manual review still required."
                    useTranslationText={true}
                    size="xs"
                    weight="bold"
                    style={styles.accessibilityScopeTitle}
                  />
                  <CustomText
                    text="Axio only checks activity titles and descriptions for HTML accessibility signals. Images, videos, slides, PDFs, and external multimedia are not evaluated — verify those manually before publishing."
                    useTranslationText={true}
                    size="xs"
                    weight="normal"
                    style={styles.accessibilityScopeText}
                  />
                </View>
              </View>
            )}
            <CustomText
              text={`${dimension.issues} ${dimension.issues === 1 ? "item" : "items"}`}
              useTranslationText={false}
              size="xs"
              weight="bold"
              style={styles.issueCountText}
            />
            <View style={styles.issueList}>
              {emptyStateMessage ? (
                <View style={styles.emptyState}>
                  <CustomText
                    text={emptyStateMessage}
                    useTranslationText={false}
                    size="s"
                    weight="normal"
                    style={styles.emptyStateText}
                  />
                </View>
              ) : dimension.issuesWithEntity && dimension.issuesWithEntity.length > 0 ? (
                dimension.issuesWithEntity.map((issue) => (
                  <View key={issue.id} style={styles.issueItem}>
                    <View style={styles.issueBullet} />
                    <CustomText
                      text={issue.description}
                      useTranslationText={false}
                      size="s"
                      weight="normal"
                      style={styles.issueItemText}
                    />
                    {issue.entityId && issue.entityId.trim() !== "" && onJumpToEntity && (
                      <View style={styles.issueActionRow}>
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`Jump to ${issue.title || dimension.label}`}
                          onPress={() => onJumpToEntity(issue.entityId!, dimension.id)}
                          style={(state) => [styles.jumpToButton, getJumpToButtonStateStyle(state)]}>
                          <CustomText
                            text="Jump to"
                            useTranslationText={false}
                            size="xs"
                            weight="medium"
                            style={styles.jumpToButtonText}
                          />
                          <ArrowRightIcon size={14} color={colors.grey[700]} />
                        </Pressable>
                      </View>
                    )}
                  </View>
                ))
              ) : (
                dimension.issuesList.map((issue, issueIndex) => (
                  <View key={`${dimension.id}-${issueIndex}`} style={styles.issueItem}>
                    <View style={styles.issueBullet} />
                    <CustomText
                      text={issue}
                      useTranslationText={false}
                      size="s"
                      weight="normal"
                      style={styles.issueItemText}
                    />
                  </View>
                ))
              )}
            </View>
          </View>
        </MotiView>
      )}
    </Pressable>
  );
}

function getButtonStateStyle({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) {
  if (pressed) {
    return styles.dimensionButtonPressed;
  }

  if (hovered) {
    return styles.dimensionButtonHovered;
  }

  return undefined;
}

function getJumpToButtonStateStyle({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) {
  if (pressed) {
    return styles.jumpToButtonPressed;
  }

  if (hovered) {
    return styles.jumpToButtonHovered;
  }

  return undefined;
}

function getEmptyStateMessage(dimension: GapAnalysisDimension): string | null {
  const hasRenderableIssues = dimension.issuesList.length > 0 || (dimension.issuesWithEntity?.length ?? 0) > 0;

  if (hasRenderableIssues) {
    return null;
  }

  if (dimension.issues > 0) {
    return `No ${dimension.label} issue details are available for this run.`;
  }

  return `No ${dimension.label} issues found in this run.`;
}

const styles = StyleSheet.create({
  rail: {
    flex: 1,
    minHeight: 0,
    backgroundColor: colors.white,
  },
  summaryHeader: {
    minHeight: 74,
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    backgroundColor: colors.grey[25],
  },
  summaryScoreGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  summaryScore: {
    color: colors.grey[900],
    fontSize: 14,
    lineHeight: 18,
  },
  dimensionList: {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({
          overflowY: "auto",
          overflowX: "hidden",
        } as unknown as ViewStyle)
      : {}),
  },
  dimensionButton: {
    position: "relative",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    paddingHorizontal: 24,
    paddingVertical: 18,
    backgroundColor: colors.white,
  },
  dimensionButtonSelected: {
    backgroundColor: colors.grey[50],
  },
  dimensionButtonHovered: {
    backgroundColor: colors.grey[25],
  },
  dimensionButtonPressed: {
    backgroundColor: colors.grey[100],
  },
  selectedAccent: {
    position: "absolute",
    left: 0,
    top: 10,
    bottom: 10,
    width: 2,
    borderTopRightRadius: 999,
    borderBottomRightRadius: 999,
  },
  dimensionTopRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  dimensionDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    flexShrink: 0,
  },
  dimensionLabelGroup: {
    minWidth: 0,
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  dimensionLabel: {
    color: colors.grey[900],
    flexShrink: 1,
  },
  dimensionHelpIcon: {
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  dimensionTooltip: {
    backgroundColor: colors.grey[900],
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    shadowColor: colors.grey[900],
    shadowOpacity: 0.18,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
  },
  dimensionTooltipText: {
    color: colors.white,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "600",
    textAlign: "left",
  },
  dimensionScoreGroup: {
    marginLeft: "auto",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  issuePanelContainer: {
    marginTop: 12,
    marginHorizontal: -24,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    overflow: "hidden",
  },
  issuePanelBody: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: colors.grey[25],
    gap: 12,
  },
  accessibilityScopeNotice: {
    marginBottom: 8,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.warning[300],
    backgroundColor: colors.warning[25],
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  accessibilityScopeIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  accessibilityScopeTextGroup: {
    flex: 1,
    gap: 4,
  },
  accessibilityScopeTitle: {
    color: colors.grey[900],
    lineHeight: 18,
  },
  accessibilityScopeText: {
    color: colors.grey[700],
    lineHeight: 18,
  },
  issueCountText: {
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontSize: 11,
    lineHeight: 14,
  },
  issueList: {
    gap: 10,
  },
  issueItem: {
    borderLeftWidth: 2,
    borderLeftColor: colors.grey[200],
    paddingLeft: 12,
    gap: 8,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  issueBullet: {
    width: 5,
    height: 5,
    borderRadius: 99,
    backgroundColor: colors.grey[600],
    marginTop: 7,
    flexShrink: 0,
  },
  issueItemText: {
    color: colors.grey[900],
    lineHeight: 20,
    flex: 1,
  },
  emptyState: {
    borderLeftWidth: 2,
    borderLeftColor: colors.grey[200],
    paddingLeft: 12,
  },
  emptyStateText: {
    color: colors.grey[700],
    lineHeight: 20,
  },
  issueActionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  jumpToButton: {
    minHeight: 28,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
    backgroundColor: colors.white,
    paddingHorizontal: 10,
    paddingVertical: 5,
    shadowColor: colors.grey[900],
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
  },
  jumpToButtonHovered: {
    backgroundColor: colors.grey[50],
    transform: [{ scale: 1.015 }],
    shadowOpacity: 0.12,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
  },
  jumpToButtonPressed: {
    backgroundColor: colors.grey[100],
    transform: [{ scale: 0.985 }],
  },
  jumpToButtonText: {
    color: colors.grey[700],
  },
});

export default GapAnalysisDimensionRail;
